import { get } from 'lodash';

import { ACCOUNT_BSB_QUESTIONS, FIELD_PATHS, LIABILITY_QUESTIONS, LIABILITY_TYPE } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Owner, PostProductSelectContract } from '@funnels/types';
import { SummaryStringDisplay } from '@funnels/ui-components';
import { Accordion, FunnelGrid, FunnelGridUnit } from '@funnels/ui-components/Common';
import {
  capitalizeForEnumValue,
  formatNumberToStringPrecision,
  getApplicantsFullnameFromOwners,
  getQuestionDisplayFields,
} from '@funnels/utils';

interface LiabilityAccountDetailsSummaryProps {
  liabilityIndex: number;
  liabilityAccountContract?: PostProductSelectContract;
  liabilityContract?: PostProductSelectContract;
  handleEditClick?: () => void;
}

export const LiabilityAccountDetailsSummary = ({
  liabilityIndex,
  handleEditClick,
  liabilityAccountContract,
  liabilityContract,
}: LiabilityAccountDetailsSummaryProps) => {
  const [formValues] = useFormValues();
  const applicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const liabilityType: LIABILITY_TYPE = get(formValues, FIELD_PATHS.LIABILITY_TYPE(liabilityIndex));
  const liabilityTypeLabel = capitalizeForEnumValue(liabilityType);
  const liabilityOwners: Owner[] = get(formValues, FIELD_PATHS.LIABILITY_OWNERS(liabilityIndex), []);
  const ownerNames = getApplicantsFullnameFromOwners(liabilityOwners, applicants);
  const hasQuestion = getQuestionDisplayFields<ACCOUNT_BSB_QUESTIONS>(
    (liabilityAccountContract?.questions as ACCOUNT_BSB_QUESTIONS[]) || []
  );
  const hasAnnualInterestRateQuestion = getQuestionDisplayFields<LIABILITY_QUESTIONS>(
    (liabilityContract?.questions as LIABILITY_QUESTIONS[]) || []
  );
  return (
    <Accordion headerText="Account details" onPrimaryButtonClick={handleEditClick} primaryButtonLabel="Edit">
      <FunnelGrid>
        <FunnelGridUnit my="xs" size={1 / 2}>
          <SummaryStringDisplay title="Owner" value={ownerNames.join(' & ')} />
        </FunnelGridUnit>
        <FunnelGridUnit my="xs" size={1 / 2}>
          <SummaryStringDisplay title="Account type" value={liabilityTypeLabel} />
        </FunnelGridUnit>
        <FunnelGridUnit my="xs" size={1 / 2}>
          <SummaryStringDisplay
            title="Institution"
            value={get(formValues, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(liabilityIndex))}
          />
        </FunnelGridUnit>
        <FunnelGridUnit my="xs" size={1 / 2}>
          <SummaryStringDisplay
            title="Credit limit"
            value={`$${formatNumberToStringPrecision(
              get(formValues, FIELD_PATHS.LIABILITY_CREDIT_LIMIT(liabilityIndex))
            )}`}
          />
        </FunnelGridUnit>
        <FunnelGridUnit my="xs" size={1 / 2}>
          <SummaryStringDisplay
            title="Balance"
            value={`$${formatNumberToStringPrecision(
              get(formValues, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(liabilityIndex))
            )}`}
          />
        </FunnelGridUnit>
        {hasQuestion.ACCOUNT_BSB && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay title="BSB" value={get(formValues, FIELD_PATHS.LIABILITY_BSB(liabilityIndex))} />
          </FunnelGridUnit>
        )}
        {hasQuestion.ACCOUNT_NUMBER && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Account number"
              value={get(formValues, FIELD_PATHS.LIABILITY_ACCOUNT_NUMBER(liabilityIndex))}
            />
          </FunnelGridUnit>
        )}
        {hasAnnualInterestRateQuestion.ANNUAL_INTEREST_RATE && (
          <FunnelGridUnit my="xs" size={1 / 2}>
            <SummaryStringDisplay
              title="Interest rate"
              value={get(formValues, FIELD_PATHS.LIABILITY_INTEREST_RATE(liabilityIndex))}
            />
          </FunnelGridUnit>
        )}
      </FunnelGrid>
    </Accordion>
  );
};
